<script setup lang="ts">
import PageContainer from "~/components/ui/PageContainer.vue";
import paperPattern from "~/features/computer/data/paper-pattern-2026.json";
import { buildComputerCollectionSchema } from "~/features/computer/data/seo/computer-schema";

const chapterLabels: Record<number, string> = {
  1: "Software Development",
  2: "Python",
  3: "Algorithms",
  4: "Data Structures",
  5: "Data Analytics",
  6: "Emerging Technologies",
  7: "Legal and Ethical Aspects",
  8: "Digital Literacy",
  9: "Entrepreneurship",
};

const chapterLinks: Record<number, string> = {
  1: "/11th/computer/sdlc/",
  2: "/11th/computer/python/",
  3: "/11th/computer/algorithm/",
  4: "/11th/computer/data-structures/",
  5: "/11th/computer/data-analytics/",
  6: "/11th/computer/emerging-tech/",
  7: "/11th/computer/legal-ethics/",
  8: "/11th/computer/digital-literacy/",
  9: "/11th/computer/entrepreneurship/",
};

const priorityBlocks = [
  {
    label: "Critical",
    icon: "HOT",
    title: "Python",
    detail: "Highest weight",
    tone: "border-amber-300 bg-gradient-to-br from-amber-100 via-orange-50 to-white dark:border-amber-800 dark:from-amber-950/40 dark:via-slate-950 dark:to-slate-900",
  },
  {
    label: "High",
    icon: "HIGH",
    title: "Algorithms, Software Development",
    detail: "Core exam coverage",
    tone: "border-sky-300 bg-gradient-to-br from-sky-100 via-cyan-50 to-white dark:border-sky-800 dark:from-sky-950/40 dark:via-slate-950 dark:to-slate-900",
  },
  {
    label: "Medium",
    icon: "MED",
    title: "Data Structures",
    detail: "Strong support chapter",
    tone: "border-stone-300 bg-gradient-to-br from-stone-100 via-stone-50 to-white dark:border-slate-700 dark:from-slate-900 dark:via-slate-950 dark:to-slate-900",
  },
  {
    label: "Low",
    icon: "LOW",
    title: "Remaining Chapters",
    detail: "MCQs only",
    tone: "border-slate-300 bg-gradient-to-br from-slate-100 via-white to-white dark:border-slate-700 dark:from-slate-900 dark:via-slate-950 dark:to-slate-900",
  },
];

const longQuestions = paperPattern.sections.part_2_subjective.long_questions.questions.map((item) => ({
  questionNo: item.q,
  chapter: item.chapter,
  label: chapterLabels[item.chapter],
}));

const shortBlocks = paperPattern.sections.part_2_subjective.short_questions.map((item) => ({
  questionNo: item.question_no,
  attempt: item.attempt,
  chapters: item.chapter_distribution.chapters.map((chapter) => ({
    chapter,
    label: chapterLabels[chapter],
  })),
}));

const mcqRows = paperPattern.sections.part_1_objective.details.chapters.map((chapter, index) => ({
  chapter,
  label: chapterLabels[chapter],
  mcqs: paperPattern.sections.part_1_objective.details.distribution[index],
}));

const importantQuestionHooks = [
  { title: "Python programs", to: "/11th/computer/python/solved-exercise/" },
  { title: "Algorithms", to: "/11th/computer/algorithm/" },
  { title: "SDLC", to: "/11th/computer/sdlc/" },
  { title: "Data structures", to: "/11th/computer/data-structures/" },
];

const relatedLinks = [
  { title: "Paper Pattern", to: "/11th/computer/paper-pattern/" },
  { title: "Model Papers", to: "/11th/computer/practice/" },
  { title: "Past Papers", to: "/11th/computer/guess-paper/" },
  { title: "Chapter Notes", to: "/11th/computer/notes/" },
];

const faqs = [
  {
    question: "1st year computer pairing scheme kya hoti hai?",
    answer:
      "Pairing scheme yeh batati hai ke paper mein kis chapter se kaun se questions aayenge. Is se aapko pata chalta hai ke kis topic ko zyada prepare karna hai.",
  },
  {
    question: "1st year computer pairing scheme 2026 kis board ke liye hai?",
    answer:
      "Yeh pairing scheme Punjab Board ke liye hoti hai jo Lahore, Gujranwala, Multan aur baaki boards follow karte hain.",
  },
  {
    question: "Pairing scheme aur important questions mein kya difference hai?",
    answer:
      "Pairing scheme chapter mapping batati hai jab ke important questions us mapping ke base par selected questions hote hain jo exam mein aane ke chances rakhte hain.",
  },
  {
    question: "Pairing scheme kya hoti hai?",
    answer: "Pairing scheme batati hai ke paper me kis chapter se questions aate hain aur kin blocks me aate hain.",
  },
  {
    question: "Long questions kis chapter se aate hain?",
    answer: "Is flow me long questions fixed hain: Q5 Ch 2, Q6 Ch 3, Q7 Ch 4, Q8 Ch 5, aur Q9 Ch 7 se aate hain.",
  },
  {
    question: "Short questions ka pattern kya hai?",
    answer: "Short questions 3 blocks me hotay hain: Q2, Q3, Q4. Har block me grouped chapters hote hain aur 6 questions attempt karne hotay hain.",
  },
  {
    question: "Pairing scheme aur paper pattern me kya difference hai?",
    answer: "Paper pattern batata hai paper ka structure kya hai. Pairing scheme batati hai ke kis chapter se questions uthte hain.",
  },
  {
    question: "Kya sirf important chapters se pass ho sakte hain?",
    answer: "Pass ke liye Ch 2 aur basic MCQs minimum route hai, lekin 60-70% ke liye Ch 2, 3, 4, 5 zyada practical shortcut hain.",
  },
];

function openPrintDialog() {
  if (import.meta.client) {
    window.print();
  }
}

usePageSeo({
  title: "11th Computer Pairing Scheme 2026 (Punjab Board) | Ustaad",
  description:
    "11th computer pairing scheme 2026 Punjab Board decision page: what to study, what to skip, long questions pattern, short questions pairing, MCQs distribution and printable scheme.",
  keywords:
    "11th computer pairing scheme 2026, 1st year computer pairing scheme, pairing scheme Punjab board",
  faqs,
  breadcrumbs: [
    { name: "Home", item: "/" },
    { name: "11th Computer", item: "/11th/computer/" },
    { name: "Pairing Scheme", item: "/11th/computer/pairing-scheme/" },
  ],
  schemaType: "CollectionPage",
  schemaProperties: buildComputerCollectionSchema({
    about: "11th Computer Pairing Scheme 2026",
    items: [
      { name: "Chapter 2 Python", path: chapterLinks[2] },
      { name: "Chapter 3 Algorithms", path: chapterLinks[3] },
      { name: "Chapter 4 Data Structures", path: chapterLinks[4] },
      { name: "Chapter 5 Data Analytics", path: chapterLinks[5] },
      { name: "Chapter 7 Legal and Ethical Aspects", path: chapterLinks[7] },
      { name: "Paper Pattern", path: "/11th/computer/paper-pattern/" },
      { name: "Important Questions", path: "/11th/computer/important-questions/" },
    ],
  }),
});
</script>

<template>
  <PageContainer>
    <section class="rounded-[30px] border border-slate-200 bg-gradient-to-br from-amber-50 via-white to-sky-50 px-5 py-6 shadow-sm dark:border-slate-800 dark:from-slate-950 dark:via-slate-900 dark:to-slate-950 md:px-8 md:py-8 print:hidden">
      <p class="text-xs font-semibold uppercase tracking-[0.24em] text-sky-700">Kon se chapters se questions aate hain - shortcut guide</p>
      <h1 class="mt-3 max-w-3xl text-3xl font-bold tracking-tight text-slate-950 dark:text-white md:text-5xl">11th Computer Pairing Scheme 2026 (Punjab Board)</h1>

      <div class="mt-5 rounded-[24px] border border-emerald-200 bg-emerald-50 p-5 dark:border-emerald-900/40 dark:bg-emerald-950/20">
        <p class="text-sm font-medium text-slate-700 dark:text-slate-300">Agar aap sirf yeh karein:</p>
        <div class="mt-3 space-y-2 text-sm leading-7 text-slate-900 dark:text-slate-100">
          <p>- Chapter 2 (Python)</p>
          <p>- Chapter 3 (Algorithms)</p>
          <p>- Chapter 4</p>
          <p>- Chapter 5</p>
        </div>
        <p class="mt-4 text-base font-semibold text-emerald-700">Aap paper ka 60-70% cover kar sakte hain.</p>
      </div>

      <div class="mt-6 flex flex-wrap gap-3">
        <a
          href="#full-scheme"
          class="inline-flex items-center justify-center rounded-full bg-slate-950 px-5 py-3 text-sm font-semibold text-white transition hover:bg-sky-700"
        >
          View Full Scheme
        </a>
        <button
          type="button"
          class="inline-flex items-center justify-center rounded-full border border-slate-300 bg-white px-5 py-3 text-sm font-semibold text-slate-900 transition hover:border-sky-300 hover:text-sky-700 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-100 dark:hover:border-sky-600 dark:hover:text-sky-300"
          @click="openPrintDialog"
        >
          Download PDF
        </button>
      </div>
    </section>

    <section class="rounded-[28px] border border-slate-200 bg-white p-5 dark:border-slate-800 dark:bg-slate-900 md:p-6">
      <div class="flex items-end justify-between gap-3">
        <div>
          <p class="text-xs font-semibold uppercase tracking-[0.22em] text-slate-500 dark:text-slate-400">Priority Snapshot</p>
          <h2 class="mt-2 text-2xl font-semibold tracking-tight text-slate-950 dark:text-white">What to study first</h2>
        </div>
      </div>

      <div class="mt-5 grid gap-4 md:grid-cols-2 xl:grid-cols-4">
        <article
          v-for="item in priorityBlocks"
          :key="item.title"
          :class="['rounded-[22px] border p-4', item.tone]"
        >
          <p class="text-xs font-semibold uppercase tracking-[0.18em] text-slate-500 dark:text-slate-400">{{ item.icon }} {{ item.label }}</p>
          <h3 class="mt-2 text-lg font-semibold tracking-tight text-slate-950 dark:text-white">{{ item.title }}</h3>
          <p class="mt-2 text-sm text-slate-600 dark:text-slate-300">{{ item.detail }}</p>
        </article>
      </div>
    </section>

    <section id="full-scheme" class="rounded-[28px] border border-slate-200 bg-white p-5 dark:border-slate-800 dark:bg-slate-900 md:p-6">
      <div>
        <p class="text-xs font-semibold uppercase tracking-[0.22em] text-slate-500 dark:text-slate-400">Long Questions</p>
        <h2 class="mt-2 text-2xl font-semibold tracking-tight text-slate-950 dark:text-white">Long Questions Pattern (Most Important)</h2>
      </div>
      <p class="mt-3 text-sm leading-7 text-slate-600 dark:text-slate-300">Yeh fixed pattern hota hai - yahan se direct questions aate hain.</p>

      <div class="mt-5 grid gap-4 md:grid-cols-2 xl:grid-cols-5">
        <article
          v-for="item in longQuestions"
          :key="item.questionNo"
          class="rounded-[22px] border border-slate-200 bg-slate-50 p-4 dark:border-slate-800 dark:bg-slate-950"
        >
          <h3 class="text-xl font-semibold text-slate-950 dark:text-white">{{ item.questionNo }}</h3>
          <p class="mt-3 text-lg font-semibold text-slate-900 dark:text-slate-100">Chapter {{ item.chapter }}</p>
          <NuxtLink :to="chapterLinks[item.chapter]" class="mt-1 block text-sm leading-6 text-sky-700 hover:text-sky-900 dark:text-sky-300 dark:hover:text-sky-200">{{ item.label }}</NuxtLink>
        </article>
      </div>
    </section>

    <section class="rounded-[28px] border border-slate-200 bg-white p-5 dark:border-slate-800 dark:bg-slate-900 md:p-6">
      <div>
        <p class="text-xs font-semibold uppercase tracking-[0.22em] text-slate-500 dark:text-slate-400">Short Questions</p>
        <h2 class="mt-2 text-2xl font-semibold tracking-tight text-slate-950 dark:text-white">Short Questions Pairing Scheme</h2>
      </div>
      <p class="mt-3 text-sm leading-7 text-slate-600 dark:text-slate-300">Har block se 6 questions attempt karne hote hain.</p>

      <div class="mt-5 grid gap-4 lg:grid-cols-3">
        <article
          v-for="item in shortBlocks"
          :key="item.questionNo"
          class="rounded-[22px] border border-slate-200 bg-slate-50 p-5 dark:border-slate-800 dark:bg-slate-950"
        >
          <div class="flex items-start justify-between gap-3">
            <h3 class="text-xl font-semibold text-slate-950 dark:text-white">{{ item.questionNo }}</h3>
            <span class="rounded-full border border-slate-300 bg-white px-3 py-1 text-xs font-semibold uppercase tracking-[0.16em] text-slate-600 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-300">
              {{ item.attempt }}
            </span>
          </div>
          <div class="mt-4 space-y-2 text-sm leading-7 text-slate-700 dark:text-slate-300">
            <p v-for="chapter in item.chapters" :key="`${item.questionNo}-${chapter.chapter}`">
              - Chapter {{ chapter.chapter }}
              <NuxtLink :to="chapterLinks[chapter.chapter]" class="text-sky-700 hover:text-sky-900 dark:text-sky-300 dark:hover:text-sky-200">({{ chapter.label }})</NuxtLink>
            </p>
          </div>
        </article>
      </div>
    </section>

    <section class="rounded-[28px] border border-slate-200 bg-white p-5 dark:border-slate-800 dark:bg-slate-900 md:p-6">
      <div>
        <p class="text-xs font-semibold uppercase tracking-[0.22em] text-slate-500 dark:text-slate-400">MCQs</p>
        <h2 class="mt-2 text-2xl font-semibold tracking-tight text-slate-950 dark:text-white">MCQs Distribution (All Chapters)</h2>
      </div>

      <div class="mt-4 overflow-hidden rounded-[18px] border border-slate-200 dark:border-slate-800">
        <table class="min-w-full divide-y divide-slate-200 text-left text-sm dark:divide-slate-800">
          <thead class="bg-slate-50 text-slate-700 dark:bg-slate-950 dark:text-slate-300">
            <tr>
              <th class="px-3 py-2 font-semibold">Chapter</th>
              <th class="px-3 py-2 font-semibold">MCQs</th>
            </tr>
          </thead>
          <tbody class="divide-y divide-slate-200 bg-white dark:divide-slate-800 dark:bg-slate-900">
            <tr v-for="row in mcqRows" :key="row.chapter">
              <td class="px-3 py-2 text-slate-900 dark:text-slate-100">
                <div class="text-sm font-medium leading-5">Ch {{ row.chapter }}</div>
                <NuxtLink :to="chapterLinks[row.chapter]" class="text-[11px] leading-4 text-sky-700 hover:text-sky-900 dark:text-sky-300 dark:hover:text-sky-200">{{ row.label }}</NuxtLink>
              </td>
              <td class="px-3 py-2 font-medium text-slate-900 dark:text-slate-100">{{ row.mcqs }}</td>
            </tr>
          </tbody>
        </table>
      </div>
    </section>

    <section class="grid gap-4 xl:grid-cols-[1.15fr_0.85fr]">
      <div class="rounded-[28px] border border-slate-200 bg-slate-950 p-5 text-white md:p-6">
        <p class="text-xs font-semibold uppercase tracking-[0.22em] text-sky-300">Smart Study Strategy</p>
        <h2 class="mt-2 text-2xl font-semibold tracking-tight">What to study, what to skip</h2>
        <div class="mt-5 space-y-4 text-sm leading-7 text-slate-200">
          <div>
            <p class="font-semibold text-white">For 70% marks:</p>
            <p>- Focus on Ch 2, 3, 4, 5</p>
          </div>
          <div>
            <p class="font-semibold text-white">For pass:</p>
            <p>- Ch 2 + basic MCQs</p>
          </div>
          <div>
            <p class="font-semibold text-white">Low priority:</p>
            <p>- Ch 7, 8, 9 (sirf MCQs ke liye)</p>
          </div>
        </div>
      </div>

      <div class="rounded-[28px] border border-slate-200 bg-white p-5 dark:border-slate-800 dark:bg-slate-900 md:p-6">
        <p class="text-xs font-semibold uppercase tracking-[0.22em] text-slate-500 dark:text-slate-400">Important Questions</p>
        <h2 class="mt-2 text-2xl font-semibold tracking-tight text-slate-950 dark:text-white">Important Questions (Based on Scheme)</h2>
        <div class="mt-5 space-y-3">
          <NuxtLink
            v-for="item in importantQuestionHooks"
            :key="item.to"
            :to="item.to"
            class="block rounded-[18px] border border-slate-200 bg-slate-50 px-4 py-3 text-sm font-medium text-slate-900 transition hover:border-sky-300 hover:bg-white dark:border-slate-800 dark:bg-slate-950 dark:text-slate-100 dark:hover:border-sky-700 dark:hover:bg-slate-900"
          >
            {{ item.title }}
          </NuxtLink>
        </div>
      </div>
    </section>

    <section class="rounded-[28px] border border-slate-200 bg-white p-5 dark:border-slate-800 dark:bg-slate-900 md:p-6">
      <div>
        <p class="text-xs font-semibold uppercase tracking-[0.22em] text-slate-500 dark:text-slate-400">Related Links</p>
        <h2 class="mt-2 text-2xl font-semibold tracking-tight text-slate-950 dark:text-white">Internal Next Steps</h2>
      </div>
      <div class="mt-5 grid gap-4 md:grid-cols-2 xl:grid-cols-4">
        <NuxtLink
          v-for="item in relatedLinks"
          :key="item.to"
          :to="item.to"
          class="rounded-[22px] border border-slate-200 bg-slate-50 p-5 transition hover:border-sky-300 hover:bg-white dark:border-slate-800 dark:bg-slate-950 dark:hover:border-sky-700 dark:hover:bg-slate-900"
        >
          <h3 class="text-lg font-semibold text-slate-950 dark:text-white">{{ item.title }}</h3>
        </NuxtLink>
      </div>
    </section>

    <section class="rounded-[28px] border border-slate-200 bg-white p-5 dark:border-slate-800 dark:bg-slate-900 md:p-6 print:hidden">
      <div>
        <p class="text-xs font-semibold uppercase tracking-[0.22em] text-slate-500 dark:text-slate-400">FAQ</p>
        <h2 class="mt-2 text-2xl font-semibold tracking-tight text-slate-950 dark:text-white">11th Computer Pairing Scheme FAQs</h2>
      </div>
      <div class="mt-5 space-y-4">
        <article
          v-for="faq in faqs"
          :key="faq.question"
          class="rounded-[22px] border border-slate-200 bg-slate-50 p-5 dark:border-slate-800 dark:bg-slate-950"
        >
          <h3 class="text-lg font-semibold text-slate-950 dark:text-white">{{ faq.question }}</h3>
          <p class="mt-2 text-sm leading-7 text-slate-600 dark:text-slate-300">{{ faq.answer }}</p>
        </article>
      </div>
    </section>

    <section class="rounded-[28px] border border-stone-200 bg-stone-50 px-5 py-6 dark:border-slate-800 dark:bg-slate-950 md:px-8 print:hidden">
      <div class="flex flex-wrap items-end justify-between gap-3">
        <div>
          <p class="text-xs font-semibold uppercase tracking-[0.22em] text-stone-500 dark:text-slate-400">PDF Block</p>
          <h2 class="mt-2 text-2xl font-semibold tracking-tight text-slate-950 dark:text-white">Printable layout, same scheme</h2>
        </div>
        <div class="flex flex-wrap gap-3">
          <a
            href="#printable-scheme"
            class="inline-flex items-center justify-center rounded-full bg-slate-950 px-4 py-2 text-sm font-semibold text-white transition hover:bg-sky-700"
          >
            View PDF
          </a>
          <button
            type="button"
            class="inline-flex items-center justify-center rounded-full border border-stone-300 bg-white px-4 py-2 text-sm font-semibold text-slate-900 transition hover:border-sky-300 hover:text-sky-700 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-100 dark:hover:border-sky-600 dark:hover:text-sky-300"
            @click="openPrintDialog"
          >
            Download PDF
          </button>
        </div>
      </div>
    </section>

    <section class="print:pt-0">
      <div
        id="printable-scheme"
        class="mx-auto max-w-[760px] rounded-[28px] border border-stone-200 bg-white px-6 py-8 shadow-[0_18px_60px_rgba(15,23,42,0.08)] dark:border-slate-800 dark:bg-slate-900 md:px-12 md:py-12 print:max-w-none print:rounded-none print:border-0 print:px-0 print:py-0 print:shadow-none"
      >
        <div class="border-b border-dashed border-stone-300 pb-6 dark:border-slate-700">
          <p class="text-xs font-semibold uppercase tracking-[0.24em] text-stone-500 dark:text-slate-400">Printable Pairing Scheme</p>
          <h2 class="mt-3 text-3xl font-bold tracking-tight text-slate-950 dark:text-white">11th Computer Pairing Scheme 2026 (Punjab Board)</h2>
          <p class="mt-3 text-base leading-8 text-slate-700 dark:text-slate-300">
            Shortcut route: Ch 2, 3, 4, 5 se 60-70% coverage target karo. Low chapters ko MCQs tak limit rakho.
          </p>
        </div>

        <div class="mt-8 grid gap-4 md:grid-cols-2">
          <section class="rounded-[22px] border border-stone-200 bg-stone-50 p-5 dark:border-slate-800 dark:bg-slate-950">
            <h3 class="text-xl font-semibold text-slate-950 dark:text-white">Long Questions Pattern</h3>
            <div class="mt-4 space-y-2 text-sm leading-7 text-slate-700 dark:text-slate-300">
              <p v-for="item in longQuestions" :key="`print-long-${item.questionNo}`">{{ item.questionNo }}: Chapter {{ item.chapter }}</p>
            </div>
          </section>

          <section class="rounded-[22px] border border-stone-200 bg-stone-50 p-5 dark:border-slate-800 dark:bg-slate-950">
            <h3 class="text-xl font-semibold text-slate-950 dark:text-white">Smart Study Strategy</h3>
            <div class="mt-4 space-y-2 text-sm leading-7 text-slate-700 dark:text-slate-300">
              <p>For 70%: Ch 2, 3, 4, 5</p>
              <p>For pass: Ch 2 + basic MCQs</p>
              <p>Low priority: Ch 7, 8, 9</p>
            </div>
          </section>
        </div>

        <div class="mt-10">
          <h3 class="text-2xl font-semibold tracking-tight text-slate-950 dark:text-white">Short Question Blocks</h3>
          <div class="mt-4 space-y-3 text-base leading-8 text-slate-700 dark:text-slate-300">
            <p v-for="item in shortBlocks" :key="`print-short-${item.questionNo}`">
              {{ item.questionNo }}: Chapter {{ item.chapters.map((chapter) => chapter.chapter).join(", ") }}
            </p>
          </div>
        </div>
      </div>
    </section>
  </PageContainer>
</template>
